/**
 * Signup Page - Modern Split-Screen Design
 */
import dopplerGraph from "../assets/login-graph.png";

import { Eye, EyeOff } from "lucide-react";
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ReactComponent as DopplerLogo } from "../assets/doppler_logo.svg";
import { ReactComponent as GoogleIcon } from "../assets/google.svg";
import { ReactComponent as MicrosoftIcon } from "../assets/microsoft.svg";
import signupBg from "../assets/signup-bg.jpg";
import { useAuth } from "../context/AuthContext";
import { authService } from "../services/authApi";
import { openOAuthPopup } from "../utils/oauthPopup";
import { defaultLandingPath } from "../utils/featureFlags";
import "./auth-styles.css";

export const Signup: React.FC = () => {
	const navigate = useNavigate();
	const { register, login, completeOAuthLogin } = useAuth();
	const [fullName, setFullName] = useState("");
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const [error, setError] = useState("");
	// Set when the account was created but is being HELD (DOP-295). Not an
	// error: the signup worked, and telling someone their new account "failed"
	// sends them round the form again.
	//
	// "code" means they own the mailbox and can let themselves in; "approval"
	// means a human has to. The server says which -- guessing would leave
	// someone staring at a code box that nobody will ever mail a code to.
	const [pendingMode, setPendingMode] = useState<null | "code" | "approval">(
		null,
	);
	const [code, setCode] = useState("");
	const [verifying, setVerifying] = useState(false);
	const [resendNote, setResendNote] = useState("");
	const [loading, setLoading] = useState(false);
	const [oauthLoading, setOauthLoading] = useState(false);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError("");
		setResendNote("");

		if (password !== confirmPassword) {
			setError("Passwords do not match");
			return;
		}

		if (password.length < 8) {
			setError("Password must be at least 8 characters long");
			return;
		}

		setLoading(true);

		try {
			const result = await register(email.trim(), password, fullName.trim());
			if (result?.pendingApproval) {
				const policy = await authService.getSignupPolicy();
				setPendingMode(policy === "code" ? "code" : "approval");
				return;
			}
			navigate(defaultLandingPath());
		} catch (err: any) {
			setError(err.message || "Failed to create account");
		} finally {
			setLoading(false);
		}
	};

	const handleVerifyCode = async (e: React.FormEvent) => {
		e.preventDefault();
		setError("");
		setResendNote("");
		setVerifying(true);

		try {
			await authService.verifySignupCode(email.trim(), code.trim());
			// The password is still in state from the form they just filled in,
			// so they go straight through rather than being bounced to a login
			// screen seconds after choosing it.
			await login(email.trim(), password);
			navigate(defaultLandingPath());
		} catch (err: any) {
			setError(err.message || "That code did not work. Please try again.");
		} finally {
			setVerifying(false);
		}
	};

	const handleResendCode = async () => {
		setError("");
		setResendNote("");

		try {
			await authService.resendSignupCode(email.trim());
			// Deliberately non-committal. The endpoint answers the same way for
			// an address with no account, and the copy must not promise mail we
			// may not have sent.
			setResendNote("If that address needs a code, a new one is on its way.");
		} catch (err: any) {
			setError(err.message || "We could not request a new code.");
		}
	};

	const handleGoogleSignup = async (e?: React.MouseEvent) => {
		e?.preventDefault();
		e?.stopPropagation();

		setError("");
		setOauthLoading(true);
		console.log("[Signup] Starting Google OAuth flow...");

		try {
			const result = await openOAuthPopup({
				url: authService.getGoogleOAuthUrl(),
				title: "Sign up with Google",
			});

			const { token, user } = result;
			console.log(
				"[Signup] OAuth success, applying session:",
				token.substring(0, 20) + "..."
			);
			authService.setToken(token);

			let resolvedUser = user;
			if (!resolvedUser) {
				try {
					console.log("[Signup] Fetching user profile post-OAuth...");
					resolvedUser = await authService.getCurrentUser();
				} catch (fetchError) {
          console.error("[Signup] Failed to fetch user after OAuth:", fetchError);
					throw new Error("Unable to complete signup. Please try again.");
				}
			}

			completeOAuthLogin(token, resolvedUser);

			console.log("[Signup] Navigating to upload page...");
			navigate("/upload");
		} catch (err: any) {
			console.error("[Signup] OAuth error:", err);
			setError(err.message || "Google signup failed");
		} finally {
			setOauthLoading(false);
		}
	};

	return (
		<div
			className="auth-container"
			style={{ "--signup-bg-image": `url(${signupBg})` } as React.CSSProperties}
		>
			{/* Left Side - Form */}
			<div className="auth-form-side">
				<div className="auth-logo">
					<DopplerLogo className="logo-svg" />
					<span className="logo-text">Doppler</span>
				</div>

				<div className="auth-content">
					<div className="auth-header">
						<h1 className="auth-title">Create your account</h1>
						<p className="auth-subtitle">Start your journey with us today.</p>
					</div>

					{pendingMode === "approval" && (
						<div className="auth-form">
							<div className="auth-notice" role="status">
								<p>
									Your account was created and is waiting for an administrator
									to approve it. You'll be able to sign in once it has been
									approved.
								</p>
							</div>
						</div>
					)}

					{pendingMode === "code" && (
						<form className="auth-form" onSubmit={handleVerifyCode}>
							<div className="auth-notice" role="status">
								<p>
									We emailed a 6-digit code to <strong>{email.trim()}</strong>.
									Enter it below to finish creating your account.
								</p>
							</div>

							{resendNote && !error && (
								<div className="auth-notice" role="status">
									<p>{resendNote}</p>
								</div>
							)}

							{error && (
								<div className="auth-error">
									<p>{error}</p>
								</div>
							)}

							<div className="form-group">
								<label htmlFor="signupCode" className="form-label">
									Verification code
								</label>
								<input
									id="signupCode"
									name="signupCode"
									type="text"
									inputMode="numeric"
									autoComplete="one-time-code"
									maxLength={6}
									value={code}
									onChange={(e) => setCode(e.target.value)}
									className="form-input"
									placeholder="123456"
									required
									autoFocus
								/>
							</div>

							<button type="submit" disabled={verifying} className="submit-btn">
								{verifying ? "Verifying..." : "Verify and continue"}
							</button>

							<p className="auth-footer-text">
								Didn't get it?{" "}
								<button
									type="button"
									className="auth-link"
									onClick={handleResendCode}
									style={{
										background: "none",
										border: "none",
										padding: 0,
										cursor: "pointer",
									}}
								>
									Send a new code
								</button>
							</p>
						</form>
					)}

					{pendingMode === null && (
					<form className="auth-form" onSubmit={handleSubmit}>
						{error && (
							<div className="auth-error">
								<p>{error}</p>
							</div>
						)}

						<div className="form-group">
							<label htmlFor="fullName" className="form-label">
								Full Name
							</label>
							<input
								id="fullName"
								name="fullName"
								type="text"
								value={fullName}
								onChange={(e) => setFullName(e.target.value)}
								className="form-input"
								placeholder="Enter your full name"
							/>
						</div>

						<div className="form-group">
							<label htmlFor="email" className="form-label">
								Email Address<span className="required">*</span>
							</label>
							<input
								id="email"
								name="email"
								type="email"
								autoComplete="email"
								required
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								className="form-input"
								placeholder="Enter your email address"
							/>
						</div>

						<div className="form-group">
							<label htmlFor="password" className="form-label">
								Password<span className="required">*</span>
							</label>
							<div className="input-with-icon">
								<input
									id="password"
									name="password"
									type={showPassword ? "text" : "password"}
									autoComplete="new-password"
									required
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									className="form-input"
									placeholder="Create a password (min 8 characters)"
								/>
								<button
									type="button"
									onClick={() => setShowPassword(!showPassword)}
									className="input-icon-btn"
									aria-label={showPassword ? "Hide password" : "Show password"}
								>
									{showPassword ? (
										<EyeOff className="icon" />
									) : (
										<Eye className="icon" />
									)}
								</button>
							</div>
						</div>

						<div className="form-group">
							<label htmlFor="confirmPassword" className="form-label">
								Confirm Password<span className="required">*</span>
							</label>
							<div className="input-with-icon">
								<input
									id="confirmPassword"
									name="confirmPassword"
									type={showConfirmPassword ? "text" : "password"}
									autoComplete="new-password"
									required
									value={confirmPassword}
									onChange={(e) => setConfirmPassword(e.target.value)}
									className="form-input"
									placeholder="Confirm your password"
								/>
								<button
									type="button"
									onClick={() => setShowConfirmPassword(!showConfirmPassword)}
									className="input-icon-btn"
									aria-label={
										showConfirmPassword ? "Hide password" : "Show password"
									}
								>
									{showConfirmPassword ? (
										<EyeOff className="icon" />
									) : (
										<Eye className="icon" />
									)}
								</button>
							</div>
						</div>

						<button type="submit" disabled={loading} className="submit-btn">
							{loading ? "Creating account..." : "Sign up"}
						</button>

						<div className="divider">
							<span className="divider-line"></span>
							<span className="divider-text">OR</span>
							<span className="divider-line"></span>
						</div>

						<div className="oauth-buttons">
							<button
								type="button"
								onClick={handleGoogleSignup}
								disabled={oauthLoading}
								className="oauth-btn"
							>
								<GoogleIcon className="oauth-icon" />
								<span>Google Account</span>
							</button>

						<button type="button" className="oauth-btn" disabled style={{ opacity: 0.5 }}>
								<MicrosoftIcon className="oauth-icon" />
								<span>Microsoft Account</span>
							</button>
						</div>

						<p className="auth-footer-text">
              Already have an account? <Link to="/login" className="auth-link">Log in</Link>
						</p>
					</form>
					)}
				</div>
			</div>


			<div className="auth-image-side">
				<span
					style={{
						fontSize: "36.6px",
						lineHeight: "48px",
						color: "#fff",
						background: "transparent",
					}}
				>
					Intelligent investing, built for
					<br />
					private credit
				</span>
				<img
					alt="doppler-graph"
					src={dopplerGraph}
					style={{
						marginTop: "43px",
						height: "100%",
					}}
				/>
			</div>
		</div>
	);
};

export default Signup;
